import styled from "@emotion/styled";
import {
  AccessTime,
  CheckCircle,
  Circle,
  Close,
  Star
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addSubscription, addCoupon } from "../../api/userApi";
import { selectCurrentUser } from "../../features/auth/authSelectors";
import { useCurrentUser } from "../Extra/UserContext";
import dayjs from "dayjs";
import useThemeColor from "../../utils/useTheme";
import { logoutUser } from "../../features/auth/authSlice";
import {
  countryRates,
  allowedCountries
} from "../../utils/countryRatesMapping";

const StyledTextField = styled(TextField)(({ theme }) => ({
  input: {
    color: "#FCFCFC"
  },
  "& .MuiOutlinedInput-root": {
    border: "2px dotted #A4A4A4",
    "& fieldset": {
      color: "#9CA3AF"
    },
    "&:hover fieldset": {
      borderColor: "#FCFCFC",
      border: "none"
    },
    "&.Mui-focused fieldset": {
      color: "#9CA3AF",
      borderColor: "#FCFCFC",
      border: "none"
    }
  }
}));

const SubscriptionModal = ({ open, onClose, isMobile = false }) => {
  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = useSelector(selectCurrentUser);
  const [inputCouponValue, setInputCouponValue] = useState("");
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const handleCouponChange = (event) => {
    // Apply transformations based on CPOVP, REFDS, MNGFQ requirements here
    // For example:
    // if (event.target.value.length > 23) return;
    const existing = event.target.value.toUpperCase();
    // Insert hyphens after every 5 characters
    // const formattedValue = existing.replace(/(.{5})(?!-)/g, "$1-");

    setInputCouponValue(existing);
  };
  const freeTrialExpired = dayjs().diff(dayjs(currentUser?.createdAt), "days");
  const handleSubscriptionChange = (event) => {
    setSubscriptionType(event?.target?.value || event);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  const currentCurrency = useMemo(
    () =>
      countryRates[
        allowedCountries?.includes(user?.country) ? user?.country : "US"
      ],
    [user]
  );
  const handleApplyCoupon = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (!inputCouponValue) return;
      const { sessionUrl, message } = await addCoupon(inputCouponValue);
      if (sessionUrl) window.location.href = sessionUrl;
      else {
        setSnackbarMessage(message);
        setOpenSnackbar(true);
        setTimeout(() => {
          onClose();
          handleCloseSnackbar();
        }, 3000);
      }
    } catch (err) {
      setSnackbarMessage(err.error);
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const styles = useThemeColor();
  const showDiscountedYearly =
    freeTrialExpired + 1 < 10 || !user?.isSubscriptionNeeded;

  const handleSubscribe = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const { session } = await addSubscription(
        subscriptionType,
        user?.email,
        subscriptionType === "yearly-discounted" && !showDiscountedYearly
          ? true
          : false
      );
      if (session?.url) window.location.href = session?.url;
      else {
        setSnackbarMessage(session.message);
        setOpenSnackbar(true);
        setTimeout(() => {
          onClose();
          handleCloseSnackbar();
        }, 3000);
      }
    } catch (err) {
      setSnackbarMessage(err.error);
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate the width of each box based on number of boxes
  const boxWidth = isMobile ? "100%" : "30%";

  const subscriptionBox = (
    type,
    title,
    description,
    badge = null,
    expiry = null,
    dontAddProgram = false
  ) => {
    const isSelected = subscriptionType === type;

    // Split the description into an array by new line or any delimiter you prefer
    const descriptionList = description.split("\n");

    return (
      <Box
        onClick={() => handleSubscriptionChange(type)}
        sx={{
          width: boxWidth,
          backgroundColor: isSelected ? "#2B2B2B" : "#1C1C1C",
          borderRadius: "8px",
          cursor: "pointer",
          margin: 0,
          height: isMobile ? "210px" : "300px",
          position: "relative",
          border: isSelected ? "2px solid #368AD0" : "none",
          "&:hover": {
            backgroundColor: "#2B2B2B"
          }
        }}
      >
        {/* Badge if provided */}
        {badge && (
          <Box
            display="flex"
            justifyContent="end"
            width="100%"
            alignItems="center"
            sx={{
              position: "absolute",
              top: 0,
              right: 0
            }}
          >
            <Typography
              display={"flex"}
              sx={{
                background: "#FCFCFC",
                padding: "3px 4px",
                alignItems: "center",
                gap: "5px",
                justifyContent: "center",
                borderRadius: "0 8px 0 8px"
              }}
              color="black"
            >
              <Star sx={{ fill: "#F1B44C", fontSize: "12px" }} />
              <Typography variant="caption" fontSize="10px" lineHeight={1}>
                {badge}
              </Typography>
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: "16px",
            justifyContent: "space-between"
          }}
        >
          {/* Top section - Radio and Pricing */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              mt: badge ? 2 : 0
            }}
          >
            <Radio
              checked={isSelected}
              onChange={() => handleSubscriptionChange(type)}
              value={type}
              name={`radio-${type}`}
              icon={<Circle sx={{ fill: "#FCFCFC" }} />}
              checkedIcon={<CheckCircle sx={{ fill: "#368AD0" }} />}
              sx={{ p: 0, mr: 1 }}
            />
            <Typography variant="h6" fontWeight="bold" color="#FCFCFC">
              {title}
            </Typography>
          </Box>

          {/* Middle section - Description with bullet points */}
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="body2" fontSize="12px" color="#FCFCFC">
              {/* <ul style={{ listStyleType: "disc", marginLeft: "20px" }}> */}
              {descriptionList.map((line, index) => (
                // <li key={index}>
                <Typography
                  variant="body2"
                  fontSize="12px"
                  mt={isMobile ? 0 : 1}
                  color="#FCFCFC"
                >
                  {line}
                </Typography>
                // </li>
              ))}
              {/* </ul> */}
            </Typography>
            {expiry && (
              <Box mt={1}>
                <Typography variant="caption" color="error">
                  {expiry}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Bottom section - No refunds text */}
          <Box sx={{ alignSelf: "flex-start", mb: 1 }}>
            <Typography variant="caption" color="#8A8A8A" fontStyle="italic">
              No refunds. Cancel anytime.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };
  return isMobile ? (
    open ? (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 10,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1300,
          overflowY: "auto"
        }}
      >
        <Box
          sx={{
            position: "relative",
            top: "50%",
            left: "50%",
            maxHeight: "95vh", // Add this
            overflowY: "auto",
            overflow: "auto",
            width: { md: "55%", xs: "100%" },
            borderRadius: 2,
            transform: "translate(-50%, -50%)"
          }}
        >
          <Box
            sx={{
              padding: { xs: "28px", md: 0 },
              position: "relative",
              bgcolor: "#000",
              borderRadius: "16px 16px 0 0",
              boxShadow: 24
              // overflow: "hidden"
            }}
            width={"100%"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: "url(/images/sky.png)",
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: 0.5,
                zIndex: 1
              }}
            />
            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                width: "100%"
              }}
            >
              <Box
                display="flex"
                justifyContent="end"
                alignItems="flex-end"
                width="100%"
              >
                {" "}
                <IconButton
                  sx={{
                    svg: {
                      fill: "#FCFCFC"
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
              <Box
                sx={{ padding: { sm: 0, md: "0 95px" } }}
                display={"flex"}
                gap="10px"
                flexDirection={"column"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Box
                  component="img"
                  src="/images/threepillars.png"
                  alt="base"
                  sx={{
                    width: "68px",
                    height: "68px",
                    objectFit: "cover"
                  }}
                />
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap="10px"
                  flexDirection="column"
                >
                  <Typography
                    fontWeight={600}
                    fontSize={"21px"}
                    color="#FCFCFC"
                  >
                    Keep Going!
                  </Typography>
                  {freeTrialExpired > 5 ? (
                    <>
                      <Typography
                        fontWeight={450}
                        textAlign={"center"}
                        fontSize={"15px"}
                        color="#FCFCFC"
                      >
                        Your free trial has ended.
                        <br />
                      </Typography>
                      <Typography
                        textAlign={"center"}
                        mt={-1.5}
                        fontWeight={400}
                        fontSize={"14px"}
                        color="#FCFCFC"
                      >
                        Please select a plan below to continue using the Three
                        Pillars Method Platform.
                      </Typography>
                    </>
                  ) : (
                    <></>
                  )}
                  {/* <Box
                    display="flex"
                    justifyContent={"center"}
                    alignItems="center"
                    gap={2}
                  >
                    <Typography fontSize={24}>🥗</Typography>
                    <Typography fontSize={"12px"} color="#FCFCFC">
                      <b>Meals of the day:</b> Get inspired with our daily MODs
                      and share yours with the community.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent={"center"}
                    alignItems="center"
                    gap={2}
                  >
                    <Typography fontSize={24}> 🔊</Typography>
                    <Typography fontSize={"12px"} color="#FCFCFC">
                      <b>30 Day Trainings:</b> Level up your diet with (3) 30
                      day trainings led by EC from Optimize Nutrition.
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent={"center"}
                    alignItems="center"
                    gap={2}
                  >
                    <Typography fontSize={24}> 📒</Typography>
                    <Typography fontSize={"12px"} color="#FCFCFC">
                      <b>Simplified logging:</b> We make it easy to track your
                      progress through the diet journey.
                    </Typography>
                  </Box> */}
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            //   height={"40%"}
            sx={{
              background: "#333333",
              display: "flex",
              padding: "24px 47px",
              borderRadius: "0 0 16px 16px",
              flexDirection: "column"
            }}
          >
            <FormControl component="fieldset">
              <RadioGroup
                value={subscriptionType}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "30px",
                  flexWrap: "nowrap",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {/* Monthly subscription option - always shown */}
                {subscriptionBox(
                  "monthly",
                  `${currentCurrency?.symbol}${
                    Number.isInteger(currentCurrency?.rate * 9.99)
                      ? currentCurrency?.rate * 9.99
                      : (currentCurrency?.rate * 9.99)?.toFixed(2)
                  }/Month`,
                  "– Daily MOD Access\n– #800gChallenge Diary (logging)"
                )}

                {/* If trial is NOT expired, show the special discounted yearly option */}
                {!user?.isSubscriptionNeeded
                  ? subscriptionBox(
                      "yearly",
                      `${currentCurrency?.symbol}${
                        Number.isInteger(currentCurrency?.rate * 99)
                          ? currentCurrency?.rate * 99
                          : (currentCurrency?.rate * 99)?.toFixed(2)
                      }/Year`,
                      "– Daily MOD Access\n– #800gChallenge Diary (logging)",
                      "",
                      ""
                    )
                  : showDiscountedYearly
                  ? subscriptionBox(
                      "yearly-discounted",
                      `${currentCurrency?.symbol}${
                        Number.isInteger(currentCurrency?.rate * 99)
                          ? currentCurrency?.rate * 99
                          : (currentCurrency?.rate * 99)?.toFixed(2)
                      }/Year`,
                      "– Daily MOD Access\n– #800gChallenge Diary (logging)\n– Full #800gChallenge Program (30 Days)",
                      "Save 50%",
                      !user?.isSubscriptionNeeded
                        ? ""
                        : `Offer expires in ${10 - freeTrialExpired} days!`
                    )
                  : /* If trial IS expired, show the standard yearly option */
                    subscriptionBox(
                      "yearly-discounted",
                      `${currentCurrency?.symbol}${
                        Number.isInteger(currentCurrency?.rate * 129)
                          ? currentCurrency?.rate * 129
                          : (currentCurrency?.rate * 129)?.toFixed(2)
                      }/Year`,
                      "– Daily MOD Access\n– #800gChallenge Diary (logging)\n– Full #800gChallenge Program (30 Days)",
                      "",
                      "",
                      true
                    )}

                {/* Additional information box shown when trial is active */}
                {/* {showDiscountedYearly && ( */}
                <Box
                  sx={{
                    width: boxWidth,
                    userSelect: "none",
                    backgroundColor: "#1C1C1C",
                    borderRadius: "8px",
                    padding: "20px",
                    margin: 0,
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    color: "#FCFCFC"
                  }}
                >
                  <Typography
                    variant="body2"
                    fontSize="12px"
                    textAlign="center"
                  >
                    After #800gChallenge program completion, higher levels
                    programs are one-time additional costs:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontSize="12px"
                    textAlign="center"
                    mt={1}
                  >
                    — Lazy Macros (30 Days,{" "}
                    {`${currentCurrency?.symbol}${
                      Number.isInteger(currentCurrency?.rate * 99)
                        ? currentCurrency?.rate * 99
                        : (currentCurrency?.rate * 99)?.toFixed(2)
                    }`}
                    )
                    <br />— Three Pillars Method (40 Days,{" "}
                    {`${currentCurrency?.symbol}${
                      Number.isInteger(currentCurrency?.rate * 199)
                        ? currentCurrency?.rate * 199
                        : (currentCurrency?.rate * 199)?.toFixed(2)
                    }`}
                    )
                  </Typography>
                  <Typography
                    variant="body2"
                    fontSize="12px"
                    textAlign="center"
                    mt={1}
                  >
                    These purchases unlock their program specific Diary
                    (logging).
                  </Typography>
                  <Typography
                    variant="body2"
                    fontSize="12px"
                    textAlign="center"
                    mt={1}
                  >
                    Your Diary (logging) subscription remains at the same cost
                    and billing cycle (
                    {`${currentCurrency?.symbol}${
                      Number.isInteger(currentCurrency?.rate * 9.99)
                        ? currentCurrency?.rate * 9.99
                        : (currentCurrency?.rate * 9.99)?.toFixed(2)
                    }`}
                    /month or{" "}
                    {`${currentCurrency?.symbol}${
                      Number.isInteger(currentCurrency?.rate * 99)
                        ? currentCurrency?.rate * 99
                        : (currentCurrency?.rate * 99)?.toFixed(2)
                    }`}
                    /year), although the features expand with the other
                    programs.
                  </Typography>
                </Box>
                {/* )} */}
              </RadioGroup>
            </FormControl>
            <Box mt={3}>
              <Button
                sx={{
                  background: "#368AD0",
                  color: "#FCFCFC",
                  textTransform: "none",
                  width: "100%",
                  fontWeight: "bold"
                  // height: "54px"
                }}
                onClick={handleSubscribe}
              >
                Subscribe
              </Button>
            </Box>
            <Box
              mt={2}
              mb={freeTrialExpired > 5 ? 2 : 7}
              display="flex"
              justifyContent="center"
            >
              <Button
                sx={{
                  width: "70%",
                  color: "#368AD0",
                  textTransform: "none",
                  fontWeight: "bold"
                }}
                variant="none"
                onClick={() => {
                  setCouponModalOpen(true);
                }}
              >
                Apply Coupon
              </Button>
            </Box>
            {freeTrialExpired > 5 && (
              <Box mb={7} display="flex" justifyContent="center">
                <Button
                  sx={{
                    width: "70%",
                    color: "#368AD0",
                    textTransform: "none",
                    fontWeight: "bold"
                  }}
                  variant="none"
                  onClick={() => {
                    dispatch(logoutUser());
                  }}
                >
                  Logout
                </Button>
              </Box>
            )}
          </Box>
          {couponModalOpen && (
            <Modal
              disableAutoFocus
              disableEnforceFocus
              open={couponModalOpen}
              onClose={() => {
                setCouponModalOpen(false);
                onClose();
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: "100%",
                  // height: { xs: "35%", md: "30%" },
                  // width: { xs: "65%", md: "30%" },
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  borderRadius: 3,
                  background: "#1C1C1C",
                  p: 2,
                  color: "#FCFCFC",
                  transform: "translate(-50%, -50%)"
                }}
              >
                <Box
                  display="flex"
                  width="100%"
                  justifyContent="end"
                  alignItems="flex-end"
                >
                  <IconButton
                    sx={{
                      svg: {
                        fill: "#FCFCFC"
                      }
                    }}
                    onClick={() => {
                      setCouponModalOpen(false);
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
                <Typography fontSize={"24px"} fontWeight={600}>
                  Apply Coupon
                </Typography>
                <Typography fontSize={"12px"} fontWeight={400} color="#D1D5DB">
                  Please enter the coupon code to activate the subscription
                </Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={3}
                  width={"100%"}
                  padding={2}
                >
                  <StyledTextField
                    fullWidth
                    value={inputCouponValue}
                    variant="outlined"
                    name="coupon"
                    placeholder="Enter the coupon code"
                    margin="normal"
                    onChange={handleCouponChange}
                  />
                  <Button
                    fullWidth
                    sx={{
                      backgroundColor: "#368AD0",
                      textTransform: "none",
                      fontWeight: "bold"
                    }}
                    variant="contained"
                    onClick={handleApplyCoupon}
                  >
                    Apply now
                  </Button>
                </Box>
              </Box>
            </Modal>
          )}
          {/* {couponModalOpen && (
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "300px",
              backgroundColor: "#1C1C1C",
              padding: "20px",
              borderRadius: "8px",
              zIndex: 1500
            }}
          >
            <StyledTextField
              value={inputCouponValue}
              onChange={handleCouponChange}
              placeholder="Enter Coupon Code"
              fullWidth
            />
            <Button
              sx={{
                marginTop: "16px",
                backgroundColor: "#368AD0",
                color: "#FCFCFC",
                textTransform: "none",
                width: "100%"
              }}
              onClick={handleSubscribe}
            >
              Apply Now
            </Button>
          </Box>
        )} */}
          <Snackbar
            open={openSnackbar}
            autoHideDuration={3000}
            onClose={handleCloseSnackbar}
            sx={{ top: 0 }}
          >
            <Alert
              severity={
                snackbarMessage?.includes("success" || "Success")
                  ? "success"
                  : "error"
              }
              onClose={handleCloseSnackbar}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    ) : (
      <></>
    )
  ) : (
    <Modal open={open} onClose={onClose} disableAutoFocus disableEnforceFocus>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          // overflow: "auto",
          height: { xs: "80%", md: "auto" },
          width: { md: "85%", xs: "100%", lg: "57%" },
          borderRadius: 2,
          border: "none",
          transform: "translate(-50%, -50%)"
        }}
      >
        <Box
          sx={{
            padding: { xs: "28px", md: 0 },
            position: "relative",
            bgcolor: "#000",
            borderRadius: "16px 16px 0 0",
            boxShadow: 24,
            overflow: "hidden" // Ensures pseudo-element stays within the box
          }}
          display={"flex"}
          width={"100%"}
          justifyContent={"center"}
          alignItems={"center"}
          //   height={"65%"}
          flexDirection={"column"}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: "url(/images/sky.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: { xs: 0.5, md: 0.5 }, // Set the opacity for the background image
              zIndex: 1 // Ensure it is behind the content
            }}
          />
          <Box
            sx={{
              position: "relative",
              width: "100%",
              zIndex: 2 // Content stays on top of the background
            }}
          >
            <Box
              display="flex"
              justifyContent="end"
              alignItems="flex-end"
              width="100%"
            >
              <IconButton
                sx={{
                  svg: {
                    fill: "#FCFCFC"
                  }
                }}
                onClick={onClose}
              >
                <Close />
              </IconButton>
            </Box>
            <Box
              sx={{ padding: { sm: 0, md: "10px 95px" } }}
              display={"flex"}
              gap="10px"
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box
                component="img"
                src="/images/threepillars.png"
                alt="base"
                sx={{
                  width: "68px",
                  height: "68px",
                  objectFit: "cover"
                }}
              />
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap="10px"
                flexDirection="column"
              >
                <Typography
                  textAlign="center"
                  fontWeight={500}
                  fontSize={"34px"}
                  color="#FCFCFC"
                >
                  Keep Going!
                </Typography>
                {freeTrialExpired > 5 ? (
                  <>
                    <Typography
                      fontWeight={450}
                      fontSize={"15px"}
                      color="#FCFCFC"
                    >
                      Your free trial has ended.
                      <br />
                    </Typography>
                    <Typography
                      mt={-1.5}
                      fontWeight={400}
                      fontSize={"14px"}
                      textAlign={"center"}
                      color="#FCFCFC"
                    >
                      Please select a plan below to continue using the Three
                      Pillars Method Platform.
                    </Typography>
                  </>
                ) : (
                  <></>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            background: "#333333",
            display: "flex",
            padding: "24px 47px",
            borderRadius: "0 0 16px 16px",
            flexDirection: "column"
          }}
        >
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={subscriptionType}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "30px",
                flexWrap: "nowrap",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {/* Monthly subscription option - always shown */}
              {subscriptionBox(
                "monthly",
                `${currentCurrency?.symbol}${
                  Number.isInteger(currentCurrency?.rate * 9.99)
                    ? currentCurrency?.rate * 9.99
                    : (currentCurrency?.rate * 9.99)?.toFixed(2)
                }/Month`,
                "– Daily MOD Access\n– #800gChallenge Diary (logging)"
              )}
              {/* If trial is NOT expired, show the special discounted yearly option */}
              {!user?.isSubscriptionNeeded
                ? subscriptionBox(
                    "yearly",
                    `${currentCurrency?.symbol}${
                      Number.isInteger(currentCurrency?.rate * 99)
                        ? currentCurrency?.rate * 99
                        : (currentCurrency?.rate * 99)?.toFixed(2)
                    }/Year`,
                    "– Daily MOD Access\n– #800gChallenge Diary (logging)",
                    "",
                    ""
                  )
                : showDiscountedYearly
                ? subscriptionBox(
                    "yearly-discounted",
                    `${currentCurrency?.symbol}${
                      Number.isInteger(currentCurrency?.rate * 99)
                        ? currentCurrency?.rate * 99
                        : (currentCurrency?.rate * 99)?.toFixed(2)
                    }/Year`,
                    "– Daily MOD Access\n– #800gChallenge Diary (logging)\n– Full #800gChallenge Program (30 Days)",
                    "Save 50%",
                    !user?.isSubscriptionNeeded
                      ? ""
                      : `Offer expires in ${10 - freeTrialExpired} days!`
                  )
                : /* If trial IS expired, show the standard yearly option */
                  subscriptionBox(
                    "yearly-discounted",
                    `${currentCurrency?.symbol}${
                      Number.isInteger(currentCurrency?.rate * 129)
                        ? currentCurrency?.rate * 129
                        : (currentCurrency?.rate * 129)?.toFixed(2)
                    }/Year`,
                    "– Daily MOD Access\n– #800gChallenge Diary (logging)\n– Full #800gChallenge Program (30 Days)",
                    "",
                    "",
                    true
                  )}

              {/* Additional information box shown when trial is active */}
              {/* {showDiscountedYearly && ( */}
              <Box
                sx={{
                  width: boxWidth,
                  userSelect: "none",
                  backgroundColor: "#1C1C1C",
                  borderRadius: "8px",
                  padding: "20px",
                  margin: 0,
                  height: "300px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  color: "#FCFCFC"
                }}
              >
                <Typography variant="body2" fontSize="12px" textAlign="center">
                  After #800gChallenge program completion, higher levels
                  programs are one-time additional costs:
                </Typography>
                <Typography
                  variant="body2"
                  fontSize="12px"
                  textAlign="center"
                  mt={1}
                >
                  — Lazy Macros (30 Days,{" "}
                  {`${currentCurrency?.symbol}${
                    Number.isInteger(currentCurrency?.rate * 99)
                      ? currentCurrency?.rate * 99
                      : (currentCurrency?.rate * 99)?.toFixed(2)
                  }`}
                  )
                  <br />— Three Pillars Method (40 Days,{" "}
                  {`${currentCurrency?.symbol}${
                    Number.isInteger(currentCurrency?.rate * 199)
                      ? currentCurrency?.rate * 199
                      : (currentCurrency?.rate * 199)?.toFixed(2)
                  }`}
                  )
                </Typography>
                <Typography
                  variant="body2"
                  fontSize="12px"
                  textAlign="center"
                  mt={1}
                >
                  These purchases unlock their program specific Diary (logging).
                </Typography>
                <Typography
                  variant="body2"
                  fontSize="12px"
                  textAlign="center"
                  mt={1}
                >
                  Your Diary (logging) subscription remains at the same cost and
                  billing cycle (
                  {`${currentCurrency?.symbol}${
                    Number.isInteger(currentCurrency?.rate * 9.99)
                      ? currentCurrency?.rate * 9.99
                      : (currentCurrency?.rate * 9.99)?.toFixed(2)
                  }`}
                  /month or{" "}
                  {`${currentCurrency?.symbol}${
                    Number.isInteger(currentCurrency?.rate * 99)
                      ? currentCurrency?.rate * 99
                      : (currentCurrency?.rate * 99)?.toFixed(2)
                  }`}
                  /year), although the features expand with the other programs.
                </Typography>
              </Box>
              {/* )} */}
            </RadioGroup>
          </FormControl>

          <Box mt={4} display="flex" justifyContent="center">
            <Button
              sx={{
                width: { xs: "100%", md: "70%" },
                backgroundColor: "#368AD0",
                textTransform: "none",
                fontWeight: "bold"
              }}
              variant="contained"
              onClick={handleSubscribe}
            >
              Subscribe
            </Button>
          </Box>
          <Box mt={2} display="flex" justifyContent="center">
            <Button
              sx={{
                width: "70%",
                color: "#368AD0",
                textTransform: "none",
                fontWeight: "bold"
              }}
              variant="none"
              onClick={() => {
                setCouponModalOpen(true);
              }}
            >
              Apply Coupon
            </Button>
          </Box>
          {freeTrialExpired > 5 && (
            <Box display="flex" justifyContent="center">
              <Button
                sx={{
                  width: "70%",
                  color: "#368AD0",
                  textTransform: "none",
                  fontWeight: "bold"
                }}
                variant="none"
                onClick={() => {
                  dispatch(logoutUser());
                }}
              >
                Logout
              </Button>
            </Box>
          )}
          {couponModalOpen && (
            <Modal
              disableAutoFocus
              disableEnforceFocus
              open={couponModalOpen}
              onClose={() => {
                setCouponModalOpen(false);
                onClose();
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  padding: "10px",
                  // height: { xs: "35%", md: "30%" },
                  // width: { xs: "65%", md: "30%" },
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  borderRadius: 3,
                  background: "#1C1C1C",
                  color: "#FCFCFC",
                  transform: "translate(-50%, -50%)"
                }}
              >
                <Snackbar
                  open={openSnackbar}
                  autoHideDuration={3000}
                  onClose={handleCloseSnackbar}
                >
                  <Alert
                    onClose={handleCloseSnackbar}
                    severity={
                      snackbarMessage?.includes("success" || "Success")
                        ? "success"
                        : "error"
                    }
                    sx={{ width: "100%", top: 0, right: 0 }}
                  >
                    {snackbarMessage}
                  </Alert>
                </Snackbar>
                <Box
                  display="flex"
                  justifyContent="end"
                  alignItems="flex-end"
                  width="100%"
                >
                  <IconButton
                    sx={{
                      svg: {
                        fill: "#FCFCFC"
                      }
                    }}
                    onClick={() => {
                      setCouponModalOpen(false);
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
                <Typography fontSize={"24px"} fontWeight={600}>
                  Apply Coupon
                </Typography>
                <Typography
                  sx={{
                    width: "70%",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    textAlign: "center",
                    marginTop: "10px"
                  }}
                  fontSize={"12px"}
                  fontWeight={400}
                  color="#D1D5DB"
                >
                  Please enter the coupon code to activate the subscription
                </Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={3}
                  width={"100%"}
                  padding={2}
                >
                  <StyledTextField
                    fullWidth
                    value={inputCouponValue}
                    variant="outlined"
                    name="coupon"
                    placeholder="Enter the coupon code"
                    margin="normal"
                    onChange={handleCouponChange}
                  />
                  <Button
                    fullWidth
                    sx={{
                      backgroundColor: "#368AD0",
                      textTransform: "none",
                      fontWeight: "bold"
                    }}
                    variant="contained"
                    onClick={handleApplyCoupon}
                  >
                    Apply now
                  </Button>
                </Box>
              </Box>
            </Modal>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default SubscriptionModal;
